import { BarChart } from "echarts/charts";
import { GridComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { useEffect, useRef } from "react";

echarts.use([BarChart, GridComponent, TooltipComponent, CanvasRenderer]);

export interface MetricBarPoint {
  label: string;
  value: number;
  color: string;
}

export function MetricBarChart({
  points,
  unit = "number",
}: {
  points: MetricBarPoint[];
  unit?: "number" | "percent";
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const chart = echarts.init(ref.current);
    chart.setOption({
      animationDuration: 400,
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        backgroundColor: "#182026",
        borderWidth: 0,
        textStyle: { color: "#fff", fontSize: 11 },
        valueFormatter: (value: number) =>
          unit === "percent" ? `${value.toFixed(1)}%` : value.toLocaleString(),
      },
      grid: {
        top: 14,
        right: 28,
        bottom: 18,
        left: 102,
      },
      xAxis: {
        type: "value",
        min: 0,
        max: unit === "percent" ? 100 : undefined,
        axisLabel: {
          color: "#78858d",
          fontSize: 9,
          formatter: unit === "percent" ? "{value}%" : "{value}",
        },
        splitLine: { lineStyle: { color: "#edf0f2" } },
      },
      yAxis: {
        type: "category",
        data: points.map((point) => point.label),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: "#56636b", fontSize: 9 },
      },
      series: [
        {
          type: "bar",
          barMaxWidth: 18,
          data: points.map((point) => ({
            value: point.value,
            itemStyle: { color: point.color, borderRadius: [0, 3, 3, 0] },
          })),
        },
      ],
    });

    const resize = () => chart.resize();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      chart.dispose();
    };
  }, [points, unit]);

  return <div className="chart-canvas" ref={ref} />;
}
